/**
 * Permission Refresh Utilities
 *
 * Handles refreshing user permissions after role changes
 */

import { AuthContextType } from '@/contexts/AuthContext';

interface PermissionRefreshOptions {
  showToast?: boolean;
  notifyOtherUsers?: boolean;
}

/**
 * Refreshes permissions for the current user after a role update
 */
export const refreshCurrentUserPermissions = async (
  auth: AuthContextType,
  _options: PermissionRefreshOptions = {}
): Promise<boolean> => {
  try {
    await auth.checkAuthStatus();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Handles permission refresh after updating any user
 * - Refreshes current user permissions if they were updated
 * - Shows appropriate notifications for other users
 */
export const handleUserRoleUpdatePermissions = async (
  updatedUserId: string,
  auth: AuthContextType,
  options: PermissionRefreshOptions = {}
): Promise<void> => {
  const isCurrentUser = auth.user?._id === updatedUserId;

  if (isCurrentUser) {
    // Refresh current user permissions
    const success = await refreshCurrentUserPermissions(auth, options);

    if (!success && options.showToast) {
      // Could add toast notification here if needed
    }
  } else {
    // TODO: Implement WebSocket notification system to notify the updated user
    // TODO: Or implement periodic permission checking in AuthContext

    if (options.notifyOtherUsers) {
      // Future: Send notification to updated user to refresh their session
    }
  }
};

/**
 * Utility to check if permission refresh is needed
 * This can be called periodically or on specific user actions
 */
export const shouldRefreshPermissions = (
  lastPermissionCheck: Date | null,
  refreshIntervalMs: number = 5 * 60 * 1000 // 5 minutes default
): boolean => {
  if (!lastPermissionCheck) return true;

  const now = new Date();
  const timeSinceLastCheck = now.getTime() - lastPermissionCheck.getTime();

  return timeSinceLastCheck >= refreshIntervalMs;
};

/**
 * Creates a periodic permission checker
 * This can be used to automatically refresh permissions at intervals
 */
export const createPeriodicPermissionChecker = (
  auth: AuthContextType,
  intervalMs: number = 5 * 60 * 1000 // 5 minutes default
) => {
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let lastCheck: Date | null = null;

  const start = () => {
    if (intervalId) return; // Already running

    intervalId = setInterval(async () => {
      if (
        auth.isAuthenticated &&
        shouldRefreshPermissions(lastCheck, intervalMs)
      ) {
        const success = await refreshCurrentUserPermissions(auth, {
          showToast: false,
        });
        if (success) {
          lastCheck = new Date();
        }
      }
    }, intervalMs);
  };

  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const forceCheck = () => {
    return refreshCurrentUserPermissions(auth);
  };

  return {
    start,
    stop,
    forceCheck,
  };
};

export default {
  refreshCurrentUserPermissions,
  handleUserRoleUpdatePermissions,
  shouldRefreshPermissions,
  createPeriodicPermissionChecker,
};
